import type {
    LocationRecordModel,
    UserRecordModel,
    UsersRecord
} from "@/utils/PocketBaseAdapter";
import { useUserRole as useAuthPermissions } from "@/router/permission";
import { useBooking } from "@/store/useBooking";
import { usePosSetupStore } from "@/store/posSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { setTimezone } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import { themeConfig } from "@themeConfig";
import { defineStore } from "pinia";
import type { RecordOptions } from "pocketbase";

const INACTIVITY_LOGGED_OUT_NOTICE_KEY = "showInactivityLoggedOutNotice"

const INACTIVITY_WARNING_MS = 20 * 60 * 1000
const INACTIVITY_ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "touchstart", "scroll"]

let inactivityCountdownTimer: ReturnType<typeof setInterval> | null = null;
let inactivityTrackingInitialized = false;

type ThemeName = "light" | "dark" | "system";

const normalizeThemeName = (theme?: string | null): ThemeName | null => {
  if (!theme) return null;

  const value = String(theme).trim().toLowerCase();
  if (value === "light" || value === "dark" || value === "system") {
    return value;
  }

  return null;
};

const isAsSelectedTheme = (theme?: string | null): boolean => {
  if (!theme) return false;

  const value = String(theme).trim().toLowerCase();
  return value === "as selected" || value === "asselected";
};

const applyTheme = (themeName: ThemeName) => {
  themeConfig.app.theme.value = themeName;
  localStorage.setItem(`${themeConfig.app.title}-theme`, themeName);
};

const normalizeThemeValueForDb = (theme?: string | null): "Light" | "Dark" | "System" | null => {
  if (!theme) return null;

  const value = String(theme).trim().toLowerCase();
  if (value === "light") return "Light";
  if (value === "dark") return "Dark";
  if (value === "system") return "System";

  return null;
};

export const useUserStore = defineStore("UserStore", {
  state: () => ({
    userId: pb.authStore.model?.id,
    usersCurrentLocation: (pb.authStore.model as UserRecordModel)?.homeLocation,
    currentLocation: pb.authStore.model?.homeLocation || localStorage.getItem("homeLocation"),
    currentLocationDetails: <LocationRecordModel>{},
    hasPrepArea: false,
    userInfo: {} as UsersRecord,
    usersList: ref<any[]>([]),
    timeZone: "Pacific/Auckland",
    userCreditCards: ref<any[]>([]),
    searchQuery:ref(""),
    showInactivityPrompt: ref(false),
    showInactivityLoggedOutNotice: ref(false),
    inactivityCountdown: ref(Math.floor(INACTIVITY_WARNING_MS / 1000))
  }),
  actions: {
    // 👉 Fetch users data
    async fetchUsers(params?: any) {

      params.location ??= this.usersCurrentLocation;

      let filters = "";

      if (params?.role) {
        filters += filters
          ? ` && roles ~ "${params.role}"`
          : `roles ~ "${params.role}"`;
      }

      if (params?.status) {
        filters += filters
          ? ` && status = "${params.status}"`
          : `status = "${params.status}"`;
      }

      if (params?.membership) {
        filters += filters
          ? ` && membership="${params.membership}"`
          : `membership="${params.membership}"`;
      }

      if (params?.location) {
        filters += filters
          ? ` && homeLocation="${params.location}"`
          : `homeLocation="${params.location}"`;
      }

      if (params?.q) {
        const query = `firstName ~ "${params.q}" || lastName ~ "${params.q}" || email ~ "${params.q}" || username ~ "${params.q}"`;

        filters += filters ? ` && ${query}` : query;
      }

      let sortParam = "";
      if (params?.sortBy && params?.sortBy.length) {
        const sortKey = params?.sortBy[0].key;
        const sortOrder = params?.sortBy[0].order === "desc" ? "-" : "";

        if (sortKey === "lastName") {
          sortParam = sortOrder + "lastName," + sortOrder + "firstName";
        } else {
          sortParam = sortOrder + sortKey;
        }
      }

      return await pb.collection("users").getList(params.page, params.itemsPerPage, {
        filter: filters,
        sort: sortParam || "",
        expand: "homeLocation",
      });
    },

    async fetchUsersNew(params?: any) {
      let filters = "";

      if (params?.role) {
        filters += filters
          ? ` && roles ~ "${params.role}"`
          : `roles ~ "${params.role}"`;
      }

      if (params?.status) {
        filters += filters
          ? ` && status = "${params.status}"`
          : `status = "${params.status}"`;
      }

      if (params?.membership) {
        filters += filters
          ? ` && membership="${params.membership}"`
          : `membership="${params.membership}"`;
      }

      if (params?.location) {
        filters += filters
          ? ` && homeLocation="${params.location}"`
          : `homeLocation="${params.location}"`;
      }

      if (params?.q) {
        let query = `firstName ~ "${params.q}" || lastName ~ "${params.q}" || email ~ "${params.q}" || username ~ "${params.q}"`;

        // Get accessFOBs Table
        const fobQuery: any = await pb.collection("accessFOBs").getFullList({
          filter: `fobNumber ~ "${params.q}"`,
        })

        if (fobQuery && fobQuery?.length > 0) {
          query += " || " + fobQuery.map((u: any) => `id="${u.user}"`).join(' || ')
        }

        filters += filters ? ` && (${query})` : query;
      }

      let sortParam = "";
      if (params?.sortBy && params?.sortBy.length) {
        const sortKey = params?.sortBy[0].key;
        const sortOrder = params?.sortBy[0].order === "desc" ? "-" : "";

        if (sortKey === "lastName") {
          sortParam = sortOrder + "lastName," + sortOrder + "firstName";
        } else {
          sortParam = sortOrder + sortKey;
        }
      }

      return await pb.collection("users").getList(params.page, params.itemsPerPage, {
        filter: filters,
        sort: sortParam || "",
        expand: "homeLocation,membership,renewMembership",
      });
    },


    // 👉 fetch use location details
    async getCurrentLocation() {
      try {
        if(this.currentLocation) {
          const record = await pb.collection("locations").getOne(`${this.currentLocation}`, {
            requestKey: null,
          });

          this.currentLocationDetails = record;
          this.timeZone = record.timeZoneCode || "Pacific/Auckland";
          setTimezone(this.timeZone);

          const prepAreas = await pb.collection("prepAreas").getFullList({ 
            filter: `location="${this.currentLocation}"`,
            requestKey: null,
          });
          this.hasPrepArea = prepAreas.length > 0;
        }
      } catch(e){
        console.error(e)
      }
    },

    // 👉 initialise store — sets up watcher so location changes propagate site-wide
    init() {
      watch(
        () => this.currentLocation,
        (newVal) => {
          if (newVal) this.getCurrentLocation();
        }
      );

      this.initInactivityTracking();

      if (sessionStorage.getItem(INACTIVITY_LOGGED_OUT_NOTICE_KEY)) {
        sessionStorage.removeItem(INACTIVITY_LOGGED_OUT_NOTICE_KEY);
        this.showInactivityLoggedOutNotice = true;
      }
    },

    // 👉 fetch user credit cards
    async getUserCreditCards() {
      try {
        const records = await pb.collection("bankCards").getFullList({
          filter: `user = "${this.userId}"`,
        });

        if (records?.length) this.userCreditCards = records;
      } catch(e){}
    },

    // 👉 fetch single user
    async fetchUser(id: string, options?: RecordOptions) {
      const record = await pb.collection("users").getOne(id, {
        requestKey: "userview",
        expand: "membership,renewMembership,roles",
      });

      this.userInfo = record;

      if (!this.userId) this.userId = this.userInfo?.id;

      if (record?.id && record.id === pb.authStore.model?.id) {
        await this.syncUserThemePreference(record);
      }

      return record;
    },

    async syncUserThemePreference(userData?: Partial<UserRecordModel & UsersRecord>) {
      try {
        const authUserId = pb.authStore.model?.id;
        const targetUserId = userData?.id || authUserId;

        // Theme preferences only apply to the currently authenticated user.
        if (!authUserId || !targetUserId || targetUserId !== authUserId) return;

        if (isAsSelectedTheme(userData?.preferedTheme as string)) {
          // Keep legacy behavior (theme from URL/local selection) and do not auto-apply global default.
          return;
        }

        const preferredTheme = normalizeThemeName(userData?.preferedTheme as string);
        if (preferredTheme) {
          applyTheme(preferredTheme);
          return;
        }

        const globalSetup = await pb
          .collection("globalSetup")
          .getOne("hjrxf532dcxwsdp", { requestKey: null });

        const defaultTheme = normalizeThemeName(globalSetup?.defaultTheme);
        if (!defaultTheme) return;

        const defaultThemeForDb = normalizeThemeValueForDb(globalSetup?.defaultTheme);
        if (!defaultThemeForDb) return;

        applyTheme(defaultTheme);

        await pb.collection("users").update(authUserId, {
          preferedTheme: defaultThemeForDb,
        });

        if (this.userInfo?.id === authUserId) {
          this.userInfo = {
            ...this.userInfo,
            preferedTheme: defaultThemeForDb,
          };
        }

        if (pb.authStore.model?.id === authUserId) {
          pb.authStore.model.preferedTheme = defaultThemeForDb;
        }
      } catch (e) {
        console.error("Error syncing user theme preference:", e);
      }
    },

    async fetchLoggedInUser(options?: RecordOptions) {
      try {
        const userId = pb.authStore.model?.id;

        return await this.fetchUser(userId, options);
      } catch (e) {}
    },

    async getUserList() {
      try {
        if (this.usersList.length) {
          return;
        }
        const records = await pb.collection("users").getFullList({
          fields: "id,name,email,firstName,lastName,avatar,username,leftHanded",
          filter: 'status != "Suspended"',
        });
        if (records) {
          this.usersList = records;
        }
      } catch (e) {}
    },

    shouldTrackInactivity() {
      const apps = JSON.parse(localStorage.getItem("Apps"))
      const isExemptRoute = ["/kiosk", "/pos"].some(path => window.location.pathname.startsWith(path))
      return pb.authStore.isValid && !apps?.POS?.isVisible && !isExemptRoute
    },

    // 👉 restart the 15 minute countdown from full
    resetInactivityCountdown() {
      this.inactivityCountdown = Math.floor(INACTIVITY_WARNING_MS / 1000)
      this.startInactivityCountdown()
    },

    // 👉 tick the countdown down every second, showing the prompt at 0
    startInactivityCountdown() {
      this.clearInactivityCountdownTimer()

      inactivityCountdownTimer = setInterval(() => {
        if (!this.shouldTrackInactivity()) {
          this.clearInactivityCountdownTimer()
          return
        }

        this.inactivityCountdown--
        if (this.inactivityCountdown <= 0) {
          this.showInactivityPrompt = true
          this.clearInactivityCountdownTimer()
        }
      }, 1000)
    },

    clearInactivityCountdownTimer() {
      if (inactivityCountdownTimer) {
        clearInterval(inactivityCountdownTimer)
        inactivityCountdownTimer = null
      }
    },

    // 👉 any user interaction resets the 15 minute clock, unless the prompt is already up
    handleInactivityActivity() {
      if (this.showInactivityPrompt || !this.shouldTrackInactivity()) return
      this.resetInactivityCountdown()
    },

    // 👉 wire up activity listeners once, and (re)start tracking on login/logout
    initInactivityTracking() {
      if (inactivityTrackingInitialized) return
      inactivityTrackingInitialized = true

      INACTIVITY_ACTIVITY_EVENTS.forEach(eventName => {
        window.addEventListener(eventName, this.handleInactivityActivity, { capture: true })
      })

      pb.authStore.onChange(() => {
        if (this.shouldTrackInactivity()) {
          this.resetInactivityCountdown()
        } else {
          this.clearInactivityCountdownTimer()
        }
      }, true)
    },

    stayLoggedIn() {
      this.showInactivityPrompt = false
      this.showInactivityLoggedOutNotice = false
      this.resetInactivityCountdown()
    },

    logoutForInactivity(){
      this.showInactivityPrompt = false
      this.clearInactivityCountdownTimer()
      sessionStorage.setItem(INACTIVITY_LOGGED_OUT_NOTICE_KEY, "1")
      this.finishLogout(true)
    },

    closeInactivityLoggedOutNotice(){
      this.showInactivityLoggedOutNotice = false
    },

    async logoutUser() {
      this.finishLogout(false)
    },

    async finishLogout(redirectToLogin = false) {
      const { clearPermissionStore } = useAuthPermissions();
      const userRoleStore = useUserRole();
      const { userId } = storeToRefs(userRoleStore);
      const posSetupStore = usePosSetupStore();
      const { clearBookingStore } = useBooking();

      clearPermissionStore();
      pb.authStore.clear();
      userId.value = null;
      userRoleStore.$reset();
      posSetupStore.reset();
      clearBookingStore();
      this.userId = null;
      localStorage.removeItem("homeLocation");
      sessionStorage.removeItem("userOnWayTo");
      localStorage.removeItem("isVerificationCodeSent");
      localStorage.removeItem("email");
      window.location.href = redirectToLogin ? "/login" : "/"
    },
  },
});
